import { IFieldDef, TValidatorFn } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrSalaryComponentEntity,
    PrSalaryComponentTemporalEntity,
    PrSalaryComponentTypeEntity
} from "@odata/GeneratedEntityTypes";
import { PrRegularSalaryTypeCode, PrSalaryComponentTypeCode } from "@odata/GeneratedEnums";
import { transformToODataString } from "@odata/OData.utils";
import {
    getTableTemporalAdditionalProperties,
    getTemporalAdditionalProperties,
    getTemporalDataColumns,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { getPayrollCurrencyTableFormatter } from "@pages/payroll/payroll.utils";
import {
    getFilterForTabId,
    getSalaryComponentColumns,
    getSalaryComponentFieldDefinitions,
    getSalaryEnumNamespaceNames,
    SalaryComponentTab,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import i18next from "i18next";
import { ValidationError } from "yup";

import { Sort, ValueType } from "../../../enums";
import { Model } from "../../../model/Model";
import { DATE_MAX, DATE_MIN, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef, ITableDefTabData, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import SalaryComponentFormView from "./SalaryComponentFormView";

export const getDefinitionsFactory = (inEditableWindow?: boolean, selectedIds?: number[], isCompensation?: boolean, isDpp?: boolean, isDelayedIncome?: boolean): IGetDefinition => {
    const getDefinitions: IGetDefinition = (): IDefinition => {

        const preventStoreVariant = inEditableWindow;

        const filters: string[] = [];
        if (selectedIds?.length) {
            filters.push(`not(${PrSalaryComponentEntity.Id} in (${transformToODataString(selectedIds, ValueType.Number)}))`);
        }

        if (inEditableWindow) {
            filters.push(getFilterForTabId(SalaryComponentTab.Active));
        }

        if (isDelayedIncome) {
            filters.push(`(${PrSalaryComponentEntity.TypeCode} ne '${PrSalaryComponentTypeCode.Regular}')`);
        }

        if (isCompensation) {
            filters.push(`(${PrSalaryComponentEntity.TypeCode} ne '${PrSalaryComponentTypeCode.Regular}'
             or ${PrSalaryComponentEntity.RegularSalaryTypeCode} eq '${PrRegularSalaryTypeCode.Monthly}')`);
        }

        if (isDpp) {
            filters.push(`${PrSalaryComponentEntity.RegularSalaryTypeCode} ne '${PrRegularSalaryTypeCode.Monthly}'`);
        }

        const defaultTabs: ITableDefTabData[] = [
            { id: SalaryComponentTab.Active, title: i18next.t("SalaryComponent:Tabs.Active") },
            { id: SalaryComponentTab.Inactive, title: i18next.t("SalaryComponent:Tabs.Inactive") },
            { id: SECONDARY_FILTERS_ALL, title: i18next.t("SalaryComponent:Tabs.All") }
        ];
        const tabs: ITableDefTabData[] = inEditableWindow ? null : defaultTabs;

        const tableAdditionalProperties: IFieldDef[] = [];

        if(inEditableWindow) {
            const cols = getTemporalDataColumns(getSalaryComponentColumns(SalaryComponentVariant.Employment));

            cols.forEach(col => {
                tableAdditionalProperties.push({ id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, col) });
                tableAdditionalProperties.push({ id: createPath(PrSalaryComponentEntity.TemporalPropertyBag, col) });
            });
            tableAdditionalProperties.push(...[
                { id: PrSalaryComponentEntity.TypeCode },
                { id: PrSalaryComponentEntity.RegularSalaryTypeCode },
            ]);
        } else {
            tableAdditionalProperties.push(...getTableTemporalAdditionalProperties(PrSalaryComponentEntity));
        }

        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrSalaryComponent}Table`,
            preventStoreVariant,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                    PrSalaryComponentEntity.Type,
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
                ],
                filterDefinition: {
                    [PrSalaryComponentEntity.Type]: {
                        fieldSettings: {
                            displayName: PrSalaryComponentTypeEntity.Name
                        }
                    },
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {
                        formatter: getPayrollCurrencyTableFormatter()
                    }
                },
                isValueHelp: true
            }],
            filter: filters.join(" AND "),
            initialSortBy: [{
                id: PrSalaryComponentEntity.Type,
                sort: Sort.Asc
            }, {
                id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                sort: Sort.Asc
            }],
            columns: [
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                PrSalaryComponentEntity.Type,
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
            ],
            additionalProperties: tableAdditionalProperties,
            columnDefinition: {
                [PrSalaryComponentEntity.Type]: {
                    fieldSettings: {
                        displayName: PrSalaryComponentTypeEntity.Name
                    }
                },
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {
                    formatter: getPayrollCurrencyTableFormatter()
                }
            },
            title: i18next.t("SalaryComponent:Title"),
            tabs,
            tabsSettings: {
                customFilter: getFilterForTabId
            }
        };
        const summary: ISummaryItem[] = [];

        const form: IFormDef = {
            id: `${EntityTypeName.PrSalaryComponent}Form`,
            translationFiles: getDefinitions.translationFiles,
            getItemBreadCrumbText: (storage: Model) =>
                    getItemBreadCrumbsText(storage, i18next.t("SalaryComponent:New"), i18next.t("SalaryComponent:FormTitle")),
            isDeletable: true,
            formControl: SalaryComponentFormView,
            summary,
            additionalProperties: [
                ...getTemporalAdditionalProperties(PrSalaryComponentTemporalEntity, PrSalaryComponentEntity)
            ],
            fieldDefinition: {
                ...TemporalValidityFieldsDef,
                ...getSalaryComponentFieldDefinitions(),
            },
            groups: [
                {
                    id: "main",
                    rows: [
                        [
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name) },
                            { id: PrSalaryComponentEntity.Type },
                            { id: PrSalaryComponentEntity.RegularSalaryType },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.IsBasicSalary) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Percentage) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusTaxImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusPayOutImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusDeductionImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount) }
                        ]

                    ]
                }
            ]
        };

        const getDateValidFromToCustomValidator: ((prop: string) => TValidatorFn) = (prop) => {
            return (val, args) => {
                const { storage, bindingContext } = args;
                const itemBc = bindingContext.getParent().getParent();
                const typeBc = itemBc.navigate(PrSalaryComponentEntity.Type);
                const type = storage.getValue(typeBc, { useDirectValue: false }) as PrSalaryComponentTypeCode;
                if (type === PrSalaryComponentTypeCode.Regular) {
                    return true;
                }
                const date = storage.getValue(bindingContext, { useDirectValue: false }) as Date;
                const utcDate = date ? getUtcDayjs(date) : null;
                const method = prop === PrSalaryComponentTemporalEntity.DateValidFrom ? "startOf" : "endOf";
                const decisiveDate = getUtcDayjs(val as Date)[method]("month");
                if (utcDate && !utcDate.isSame(DATE_MIN) && !utcDate.isSame(DATE_MAX) && !decisiveDate.isSame(date, "date")) {
                    return new ValidationError(i18next.t(`SalaryComponent:Form.${prop}GranularityValidation`), false, args.bindingContext.getEntityPath(true));
                }
                return true;
            };
        };

        // enhance date valid from / to validation for some types of salary components
        const fromSettings = form.fieldDefinition["TemporalPropertyBag/DateValidFrom"].validator.settings;
        if (typeof fromSettings.customValidator === "function") {
            fromSettings.customValidator = [{ validator: fromSettings.customValidator as TValidatorFn }]; // convert to ICustomValidator[]
        }
        fromSettings.customValidator.push({
            validator: getDateValidFromToCustomValidator(PrSalaryComponentTemporalEntity.DateValidFrom),
        });

        const toSettings = form.fieldDefinition["TemporalPropertyBag/DateValidTo"].validator.settings;
        if (typeof toSettings.customValidator === "function") {
            toSettings.customValidator = [{ validator: toSettings.customValidator as TValidatorFn }]; // convert to ICustomValidator[]
        }
        toSettings.customValidator.push({
            validator: getDateValidFromToCustomValidator(PrSalaryComponentTemporalEntity.DateValidTo),
        });

        return {
            entitySet: EntitySetName.PrSalaryComponents,
            table,
            form
        };
    };

    getDefinitions.translationFiles = ["SalaryComponent", "Common", ...getSalaryEnumNamespaceNames()];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrSalaryComponent, getDefinitions);